import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { asapScheduler, Observable, of } from 'rxjs';
import {
  environment,
  featureFlag,
} from '../../../environments/environment';
import { ApiService } from '../../core/http/api.service';
import { DropdownItem, JsonObject } from '../models/common.model';
import { API_URL } from './api.constant';
import { concatMap, map, take, tap } from 'rxjs/operators';
import { DepartmentNameWithMemberCount } from '@shared/models/department.model';

type DepartmentDropdownItem = DropdownItem<
  number,
  DepartmentNameWithMemberCount
>;

@Injectable({
  providedIn: 'root',
})
export class DropdownService {
  allDepartmentDropdownCache: DepartmentDropdownItem[] = [];
  private _commonCache: {
    [key: string]: DropdownGetResponse;
  } = {};

  constructor(
    private http: HttpClient,
    private httpClient: ApiService,
  ) {}

  getDropdown<Value = number, Context = unknown>(
    params: any,
    options: DropdownGetOptions = {},
  ): Observable<DropdownGetResponse<Value, Context>> {
    const cacheKey = options?.cacheKey;
    if (cacheKey) {
      const cacheValue = this.getCache(cacheKey);
      if (cacheValue) {
        return of(
          cacheValue as DropdownGetResponse<Value, Context>,
        ).pipe(take(1));
      }
    }

    return this.httpClient
      .get<
        DropdownGetResponse<Value, Context>
      >(API_URL.dropdown, params)
      .pipe(
        concatMap((data) => {
          if (cacheKey) {
            this.setCache(cacheKey, data as DropdownGetResponse);
          }
          return of(data);
        }),
      );
  }

  getAllDepartmentDropdown(
    canUseCache: any = false,
  ): Observable<DepartmentDropdownItem[]> {
    const cacheValue = this.allDepartmentDropdownCache;
    if (canUseCache && cacheValue.length) {
      return new Observable((subscriber) => {
        asapScheduler.schedule(() => {
          subscriber.next(cacheValue);
        });
      }).pipe(take<DepartmentDropdownItem[] | any>(1));
    }
    return this.getDropdown({
      type: 'department',
      is_all: true,
    }).pipe(
      map((data) => {
        // store certificate status into cache.
        this.allDepartmentDropdownCache = data[
          'department'
        ] as DepartmentDropdownItem[];
        return this.allDepartmentDropdownCache;
      }),
    );
  }

  getDropdownWithHeader(header: any, params: any): any {
    return this.http.get(environment.baseUrl + API_URL.dropdown, {
      params,
      headers: header,
    });
  }

  cacheKeyToStr(cacheKey: JsonObject) {
    return JSON.stringify(cacheKey);
  }

  getCache(cacheKey: JsonObject) {
    const cacheKeyStr = this.cacheKeyToStr(cacheKey);
    if (cacheKeyStr in this._commonCache) {
      return this._commonCache[cacheKeyStr];
    } else {
      return null;
    }
  }

  setCache(cacheKey: JsonObject, data: DropdownGetResponse) {
    const cacheKeyStr = this.cacheKeyToStr(cacheKey);
    this._commonCache[cacheKeyStr] = data;
  }

  clearCache() {
    this._commonCache = {};
  }

  memoTypeItem() {
    const memo_types = [
      {
        label: 'External',
        value: 'external',
        context: {
          display_name: 'แบบขออนุมัติภายนอก',
          display_name_en: 'External',
        },
      },
      {
        label: 'Internal',
        value: 'internal',
        context: {
          display_name: 'แบบขออนุมัติภายใน',
          display_name_en: 'Internal',
        },
      },
      {
        label: 'Purchase Request',
        value: 'purchase_request',
        context: {
          display_name: 'แบบขออนุมัติการซื้อ',
          display_name_en: 'Purchase Request',
        },
      },
      {
        label: 'Upload',
        value: 'upload',
        context: {
          display_name: 'เอกสารอัปโหลด',
          display_name_en: 'Upload',
        },
      },
    ];
    const contract_types = [
      {
        label: 'Contract',
        value: 'contract',
        context: {
          display_name: 'สัญญา',
          display_name_en: 'Contract',
        },
      },
    ];

    if (featureFlag.memo_enable && featureFlag.econtract_enable) {
      return memo_types.concat(contract_types);
    } else if (featureFlag.memo_enable) {
      return memo_types;
    } else if (featureFlag.econtract_enable) {
      return contract_types;
    } else {
      return [];
    }
  }
}

export interface DropdownResponse {
  [type: string]: {
    label: any;
    value: any;
    context?: any;
  }[];
}

export interface DropdownGetOptions {
  cacheKey?: JsonObject;
}

export type DropdownGetResponse<Value = number, Context = any> = {
  [dropdownName: string]: DropdownItem<Value, Context>[];
};
